/**
 * In dev we always want verbose logging to be off so we concentrate on only the logs we need for the
 * feature we are developing at the time.
 * In deployed environements we always want verbose logging to be on so we have all the information we need to
 * debug any issue that occured in production without needing a real user to replicate them.
 */

const verboseLoggingEnabled = process.env.VERBOSE_LOGGING === "1" || true;

const blue = (text: string) => `\x1b[34m${text}\x1b[0m`;
const red = (text: string) => `\x1b[31m${text}\x1b[0m`;
const green = (text: string) => `\x1b[32m${text}\x1b[0m`;
const yellow = (text: string) => `\x1b[33m${text}\x1b[0m`;
const orange = (text: string) => `\x1b[38;5;208m${text}\x1b[0m`;
const pink = (text: string) => `\x1b[38;5;206m${text}\x1b[0m`;
const purple = (text: string) => `\x1b[35m${text}\x1b[0m`;
const cyan = (text: string) => `\x1b[36m${text}\x1b[0m`;

export const color = {
    red,
    blue,
    green,
    yellow,
    orange,
    pink,
    purple,
    cyan,
};

export const debugStructure = (data: unknown) => {
    const getStructure = (obj: unknown, depth = 2): unknown => {
        if (depth === 0) return "...";
        if (obj === null) return "null";
        if (Array.isArray(obj)) return "array";
        if (typeof obj !== "object") return typeof obj;

        return Object.entries(obj).reduce((acc, [key, value]) => {
            acc[key] = getStructure(value, depth - 1);
            return acc;
        }, {} as Record<string, unknown>);
    };

    if (verboseLoggingEnabled) {
        console.log(
            "OfferPage Structure:",
            color.purple(JSON.stringify(getStructure(data), null, 2))
        );
    }
};

const transformObjsToStrings = (...data: unknown[]): unknown[] => {
    return data.map((item) =>
        typeof item === "object" ? JSON.stringify(item) : item
    );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const debugLog = (...data: unknown[]): void => {
    if (verboseLoggingEnabled) {
        console.log(...transformObjsToStrings(...data));
    }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const debugError = (...data: unknown[]): void => {
    console.error(red("‼️"), ...transformObjsToStrings(...data));
};

export const debugTime = (label: string): void => {
    if (verboseLoggingEnabled) {
        console.time(label);
    }
};

export const debugTimeEnd = (label: string): void => {
    if (verboseLoggingEnabled) {
        console.log("⏰", color.green(label));
        console.timeEnd(label);
    }
};

// Sometimes we want to log something even if verbose logging is off
// e.g for investigating issues in production.
// These logs should be used sparingly and only for the most important information.
export const debugLogAlways = (...data: unknown[]): void => {
    console.log(color.yellow("! "), ...transformObjsToStrings(...data));
};

export const debugTimeAlways = (label: string): void => {
    console.time(label);
};

export const debugTimeEndAlways = (label: string): void => {
    console.log(color.yellow("! "), "⏰", color.green(label));
    console.timeEnd(label);
};

export const truncateData = (data: string, maxLength: number): string => {
    return data.length < maxLength
        ? data
        : `${data.substring(0, maxLength)}...${data.substring(
              data.length - maxLength
          )}, ${data.length} characters`;
};
